import React, { useState, useEffect } from 'react';
import { API_BASE, getAuthHeaders } from '../config/api';
import './DisplayTitleEditorModal.css';

function DisplayTitleEditorModal({ isOpen, onClose, track, album, onSave }) {
  const [expandedAccordion, setExpandedAccordion] = useState({
    display: true,
    metadata: false,
    filePath: false,
  });
  const [displayTitle, setDisplayTitle] = useState('');
  const [displayArtist, setDisplayArtist] = useState('');
  const [displayAlbum, setDisplayAlbum] = useState('');
  const [metadataArtist, setMetadataArtist] = useState('');
  const [metadataAlbum, setMetadataAlbum] = useState('');
  const [metadataTitle, setMetadataTitle] = useState('');
  const [filePath, setFilePath] = useState('');
  const [pathParts, setPathParts] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [applyMode, setApplyMode] = useState(null); // 'current' | 'all-tracks' | 'all-albums'

  const splitFilePath = (path) => {
    if (!path) return [];
    
    let separator = '/';
    if (path.includes(' - ')) {
      separator = ' - ';
    } else if (path.includes(' -')) {
      separator = ' -';
    } else if (path.includes('- ')) {
      separator = '- ';
    } else if (path.includes('_')) {
      separator = '_';
    } else if (path.includes(' / ')) {
      separator = ' / ';
    } else if (path.includes('/')) {
      separator = '/';
    } else if (path.includes(' ')) {
      separator = ' ';
    }
    
    const parts = path.split(separator).map(part => part.trim()).filter(part => part.length > 0);
    
    return parts.map((part, index) => ({
      text: part,
      index,
      separator: index < parts.length - 1 ? separator : '',
    }));
  };

  useEffect(() => {
    if (isOpen && track) {
      setDisplayTitle(track.DisplayTitle || '');
      setDisplayArtist(track.DisplayArtist || track.artist || album?.artist || album?.artistName || '');
      setDisplayAlbum(track.DisplayAlbum || album?.album || album?.albumName || '');
      setMetadataArtist(track.artist || album?.artist || album?.artistName || '');
      setMetadataAlbum(track.album || album?.album || album?.albumName || '');
      setMetadataTitle(track.title || track.Title || '');
      
      const path = track.filePath || track.storagePath || track.Title || '';
      setFilePath(path);
      
      const parts = splitFilePath(path);
      setPathParts(parts);
      setApplyMode(null); // Reset apply mode when modal opens
    }
  }, [isOpen, track, album]);

  const toggleAccordion = (accordionName) => {
    setExpandedAccordion(prev => ({
      ...prev,
      [accordionName]: !prev[accordionName],
    }));
  };

  const handleFieldValueClick = (value) => {
    if (!value || value.trim() === '') return;
    const currentTitle = displayTitle || '';
    const newTitle = currentTitle 
      ? `${currentTitle} - ${value}` 
      : value;
    setDisplayTitle(newTitle);
  };

  const handlePathPartClick = (part) => {
    handleFieldValueClick(part.text);
  };

  const handleSave = async () => {
    if (!track || !album) return;
    
    if (!applyMode) {
      alert('Please select an apply option first (Apply to all tracks, or Apply to all tracks in all albums)');
      return;
    }
    
    setIsSaving(true);
    
    try {
      let result;
      if (applyMode === 'all-tracks') {
        result = await saveToAllTracks();
      } else if (applyMode === 'all-albums') {
        result = await saveToAllAlbums();
      }
      
      // Return the updated track data so parent can refresh album
      if (onSave) {
        onSave(result?.track || track);
      }
      handleClose();
    } catch (error) {
      console.error('Save error:', error);
      alert(`Failed to save changes: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const saveToCurrentTrack = async () => {
    const trackIndex = album.songs?.findIndex(s => 
      s.Id === track.Id || 
      s.Title === track.Title ||
      (s.DisplayTitle && s.DisplayTitle === track.DisplayTitle)
    );
    
    if (trackIndex === -1) {
      throw new Error('Track not found in album');
    }
    
    const metadata = {};
    // Always include DisplayTitle, even if empty, so it can be cleared
    if (displayTitle !== undefined) metadata.DisplayTitle = displayTitle;
    if (displayArtist !== undefined) metadata.DisplayArtist = displayArtist;
    if (displayAlbum !== undefined) metadata.DisplayAlbum = displayAlbum;
    if (metadataArtist !== undefined) metadata.artist = metadataArtist;
    if (metadataAlbum !== undefined) metadata.album = metadataAlbum;
    if (metadataTitle !== undefined) metadata.title = metadataTitle;
    
    console.log('💾 Saving to current track:', {
      trackIndex,
      displayTitle,
      metadata,
      trackTitle: track.Title,
      currentDisplayTitle: track.DisplayTitle
    });
    
    const collection = album.collection || 'albums-flat';
    
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/update-track-metadata`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        albumId: album.id,
        trackIndex,
        collection,
        metadata
      })
    });
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to save');
    }
    
    console.log('✅ Save response:', data);
    
    // Return the updated track data
    return data;
  };

  const saveToAllTracks = async () => {
    const collection = album.collection || 'albums-flat';
    const headers = await getAuthHeaders();

    // Update each track in the album
    const updatePromises = album.songs?.map(async (song, index) => {
      const metadata = {
        DisplayTitle: displayTitle,
      };
      if (displayArtist) metadata.DisplayArtist = displayArtist;
      if (displayAlbum) metadata.DisplayAlbum = displayAlbum;
      if (metadataArtist) metadata.artist = metadataArtist;
      if (metadataAlbum) metadata.album = metadataAlbum;
      if (metadataTitle) metadata.title = metadataTitle;

      const response = await fetch(`${API_BASE}/update-track-metadata`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          albumId: album.id,
          trackIndex: index,
          collection,
          metadata
        })
      });

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to update track');
      }
      return data;
    }) || [];

    await Promise.all(updatePromises);
    alert(`Successfully updated ${updatePromises.length} tracks!`);
  };

  const handleApplyToAllTracks = () => {
    if (!album || !displayTitle) {
      alert('Please set a DisplayTitle value first');
      return;
    }

    if (!window.confirm(`Apply DisplayTitle "${displayTitle}" to all ${album.songs?.length || 0} tracks in this album?`)) {
      return;
    }

    setApplyMode('all-tracks');
  };

  const saveToAllAlbums = async () => {
    const collection = album.collection || 'albums-flat';
    const headers = await getAuthHeaders();
    const trackTitle = track.Title || track.title || '';

    // Call API to apply to all albums
    const response = await fetch(`${API_BASE}/apply-displaytitle-to-all-albums`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        trackTitle,
        displayTitle,
        collection,
        metadata: {
          DisplayTitle: displayTitle,
          ...(displayArtist && { DisplayArtist: displayArtist }),
          ...(displayAlbum && { DisplayAlbum: displayAlbum }),
          ...(metadataArtist && { artist: metadataArtist }),
          ...(metadataAlbum && { album: metadataAlbum }),
          ...(metadataTitle && { title: metadataTitle }),
        }
      })
    });

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to update tracks');
    }

    alert(`Successfully updated ${data.updatedCount || 0} tracks across ${data.albumCount || 0} albums!`);
  };

  const handleApplyToAllAlbums = () => {
    if (!album || !displayTitle) {
      alert('Please set a DisplayTitle value first');
      return;
    }

    if (!window.confirm(`Apply DisplayTitle "${displayTitle}" to all matching tracks in all albums in the ${album.collection || 'albums-flat'} collection?\n\nThis will update tracks with the same Title as the current track.`)) {
      return;
    }

    setApplyMode('all-albums');
  };

  const handleApplyToCurrentTrack = async () => {
    if (!track || !album) return;
    
    setIsSaving(true);
    
    try {
      const result = await saveToCurrentTrack();
      
      // Return updated track data if available
      if (onSave) {
        onSave(result?.track || track);
      }
      // Don't close, let user continue editing or apply to other tracks
    } catch (error) {
      console.error('Apply to current track error:', error);
      alert(`Failed to save changes: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setDisplayTitle('');
    setDisplayArtist('');
    setDisplayAlbum('');
    setMetadataArtist('');
    setMetadataAlbum('');
    setMetadataTitle('');
    setFilePath('');
    setPathParts([]);
    setExpandedAccordion({ display: true, metadata: false, filePath: false });
    setApplyMode(null);
    onClose();
  };

  if (!isOpen || !track) {
    return null;
  }

  return (
    <div className="display-title-editor-overlay" onClick={handleClose}>
      <div
        className="display-title-editor-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="display-title-editor-header">
          <h2>✏️ Edit DisplayTitle</h2>
          <button className="display-title-editor-close" onClick={handleClose}>
            ✕
          </button>
        </div>

        <div className="display-title-editor-content">
          <div className="display-title-editor-accordion">
            <button
              className={`display-title-editor-accordion-header ${expandedAccordion.display ? 'expanded' : ''}`}
              onClick={() => toggleAccordion('display')}
            >
              <span>Display Values</span>
              <span className="accordion-icon">{expandedAccordion.display ? '▼' : '▶'}</span>
            </button>
            {expandedAccordion.display && (
              <div className="display-title-editor-accordion-content">
                <div className="display-title-editor-field">
                  <label>DisplayArtist</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={displayArtist}
                      onChange={(e) => setDisplayArtist(e.target.value)}
                      placeholder="Artist name"
                    />
                    {displayArtist && (
                      <button
                        type="button"
                        className="display-title-editor-add-button"
                        onClick={() => handleFieldValueClick(displayArtist)}
                        title={`Add "${displayArtist}" to DisplayTitle`}
                      >
                        +
                      </button>
                    )}
                  </div>
                </div>
                <div className="display-title-editor-field">
                  <label>DisplayAlbum</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={displayAlbum}
                      onChange={(e) => setDisplayAlbum(e.target.value)}
                      placeholder="Album name"
                    />
                    {displayAlbum && (
                      <button
                        type="button"
                        className="display-title-editor-add-button"
                        onClick={() => handleFieldValueClick(displayAlbum)}
                        title={`Add "${displayAlbum}" to DisplayTitle`}
                      >
                        +
                      </button>
                    )}
                  </div>
                </div>
                <div className="display-title-editor-field">
                  <label>DisplayTitle</label>
                  <input
                    type="text"
                    value={displayTitle}
                    onChange={(e) => setDisplayTitle(e.target.value)}
                    placeholder="Track title"
                    autoFocus
                  />
                </div>
              </div>
            )}
          </div>

          <div className="display-title-editor-accordion">
            <button
              className={`display-title-editor-accordion-header ${expandedAccordion.metadata ? 'expanded' : ''}`}
              onClick={() => toggleAccordion('metadata')}
            >
              <span>Metadata Values</span>
              <span className="accordion-icon">{expandedAccordion.metadata ? '▼' : '▶'}</span>
            </button>
            {expandedAccordion.metadata && (
              <div className="display-title-editor-accordion-content">
                <div className="display-title-editor-field">
                  <label>artist</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={metadataArtist}
                      onChange={(e) => setMetadataArtist(e.target.value)}
                      placeholder="Artist name"
                    />
                    {metadataArtist && (
                      <button
                        type="button"
                        className="display-title-editor-add-button"
                        onClick={() => handleFieldValueClick(metadataArtist)}
                        title={`Add "${metadataArtist}" to DisplayTitle`}
                      >
                        +
                      </button>
                    )}
                  </div>
                </div>
                <div className="display-title-editor-field">
                  <label>album</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={metadataAlbum}
                      onChange={(e) => setMetadataAlbum(e.target.value)}
                      placeholder="Album name"
                    />
                    {metadataAlbum && (
                      <button
                        type="button"
                        className="display-title-editor-add-button"
                        onClick={() => handleFieldValueClick(metadataAlbum)}
                        title={`Add "${metadataAlbum}" to DisplayTitle`}
                      >
                        +
                      </button>
                    )}
                  </div>
                </div>
                <div className="display-title-editor-field">
                  <label>title</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={metadataTitle}
                      onChange={(e) => setMetadataTitle(e.target.value)}
                      placeholder="Track title"
                    />
                    {metadataTitle && (
                      <button
                        type="button"
                        className="display-title-editor-add-button"
                        onClick={() => handleFieldValueClick(metadataTitle)}
                        title={`Add "${metadataTitle}" to DisplayTitle`}
                      >
                        +
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="display-title-editor-accordion">
            <button
              className={`display-title-editor-accordion-header ${expandedAccordion.filePath ? 'expanded' : ''}`}
              onClick={() => toggleAccordion('filePath')}
            >
              <span>File Path</span>
              <span className="accordion-icon">{expandedAccordion.filePath ? '▼' : '▶'}</span>
            </button>
            {expandedAccordion.filePath && (
              <div className="display-title-editor-accordion-content">
                <div className="display-title-editor-path-display">
                  <div className="display-title-editor-path-label">Full Path:</div>
                  <div className="display-title-editor-path-text">{filePath || 'N/A'}</div>
                </div>
                <div className="display-title-editor-path-parts">
                  <div className="display-title-editor-path-label">Click parts to add to DisplayTitle:</div>
                  <div className="display-title-editor-path-buttons">
                    {pathParts.length > 0 ? (
                      pathParts.map((part, index) => (
                        <button
                          key={index}
                          className="display-title-editor-path-button"
                          onClick={() => handlePathPartClick(part)}
                          title={`Click to add "${part.text}" to DisplayTitle`}
                        >
                          {part.text}
                          {part.separator && <span className="path-separator">{part.separator}</span>}
                        </button>
                      ))
                    ) : (
                      <div className="display-title-editor-no-parts">No path parts available</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="display-title-editor-actions">
          <button
            className="display-title-editor-button"
            onClick={handleApplyToCurrentTrack}
            disabled={isSaving}
          >
            Apply to current track
          </button>
          <button
            className={`display-title-editor-button ${applyMode === 'all-tracks' ? 'active' : ''}`}
            onClick={handleApplyToAllTracks}
            disabled={isSaving}
          >
            Apply to all tracks
          </button>
          <button
            className={`display-title-editor-button ${applyMode === 'all-albums' ? 'active' : ''}`}
            onClick={handleApplyToAllAlbums}
            disabled={isSaving}
          >
            Apply to all tracks in all albums in this collection
          </button>
          <button
            className="display-title-editor-save"
            onClick={handleSave}
            disabled={isSaving || !applyMode}
          >
            {isSaving ? '💾 Saving...' : '💾 Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DisplayTitleEditorModal;

